import { useState } from "react";
import SlideRenderer from "@/components/SlideRenderer";
import PresentationPanel from "@/components/PresentationPanel";
import SlideControls from "@/components/SlideControls";
import usePresentation from "@/hooks/usePresentation";

export default function GrowthOSPage() {
  const {
    index,
    slide,
    total,
    next,
    prev,
    goTo,
    paused,
    togglePause,
    isFirst,
    isLast,
  } = usePresentation(0);

  const [animationKey, setAnimationKey] = useState(0);
  const [staticFrame, setStaticFrame] = useState(false);

  const replay = () => {
    setStaticFrame(false);
    setAnimationKey((k) => k + 1);
  };

  const motionFrozen = paused || staticFrame;
  const fullBleed =
    slide.layout === "pricing" ||
    slide.layout === "hub-walkthrough" ||
    slide.layout === "video-slide" ||
    slide.layout === "ip-value";

  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-[#0F2442] text-white grain-overlay vignette flex flex-col"
      data-testid="growthos-page"
    >
      <div className="ambient-halo" />
      <div className="grid-bg" />

      {/* HEADER */}
      <header
        className="relative z-30 flex items-center justify-between px-4 sm:px-8 pt-3 sm:pt-4 shrink-0"
        data-testid="header-bar"
      >
        <div className="flex items-center gap-2.5" data-testid="brand-logo">
          <img
            src="/img/growthos-logo.png"
            alt="GrowthOS by Smart Marketer"
            className="h-14 sm:h-16 w-auto select-none"
            style={{
              filter: "invert(1) brightness(2)",
              opacity: 0.92,
            }}
            draggable={false}
          />
        </div>

        <div className="hidden sm:flex items-center gap-2 glass rounded-full px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] soft-glow" />
          <span className="text-[10px] tracking-[0.28em] text-white/55 font-mono uppercase">
            System Online
          </span>
        </div>
      </header>

      {/* STAGE — dispatches to per-slide layout */}
      <main
        className={`relative z-10 flex-1 min-h-0 w-full pb-[150px] sm:pb-[110px] overflow-hidden ${
          fullBleed ? "" : "lg:pr-[400px]"
        }`}
        data-testid="visualization-container"
      >
        <div
          key={`slide-${index}-${animationKey}`}
          className="slide-fade-in h-full w-full"
        >
          <SlideRenderer slide={slide} />
        </div>
      </main>

      {!fullBleed && (
        <PresentationPanel slide={slide} index={index} total={total} />
      )}

      <SlideControls
        index={index}
        total={total}
        next={next}
        prev={prev}
        goTo={goTo}
        paused={paused}
        togglePause={togglePause}
        staticFrame={staticFrame}
        toggleStatic={() => setStaticFrame((s) => !s)}
        replay={replay}
        isFirst={isFirst}
        isLast={isLast}
      />

      <style>
        {motionFrozen
          ? `
          [data-testid="connectors-svg"] animateMotion,
          [data-testid="brand-intelligence-core"] animateMotion { display: none; }
          .pulse-ring { animation: none !important; }
          .soft-glow { animation: none !important; opacity: 1 !important; }
          .shimmer { animation: none !important; background-position: 0 0 !important; }
        `
          : ""}
      </style>
    </div>
  );
}
